/**
 * ExperienceDocuments — 5-slot document bundle per experience row.
 *
 *   Single slots: joining_letter, relieving_letter, experience_letter
 *   List slots:   recommendations, additional
 *
 * Two surfaces:
 *   - <ExperienceDocsChip> — compact `+N docs` chip for UnifiedTimeline
 *     row (inline). Click opens the picker.
 *   - <ExperienceDocsCard>  — the full editor (used by both the chip
 *     popover and any card layout below the timeline).
 *
 * Uses fingerprint expKey `${company}|${title}|${start_date}` to key
 * into `candidate.experience_documents[expKey]`.
 */
import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Upload, Link2, X, Paperclip, ExternalLink } from "lucide-react";
import RedFlagChip from "@/components/candidate/RedFlagChip";

const SLOT_META = {
    joining_letter:    { label: "Joining letter",     single: true },
    relieving_letter:  { label: "Relieving letter",   single: true },
    experience_letter: { label: "Experience letter",  single: true },
    recommendations:   { label: "Recommendation letters", single: false },
    additional:        { label: "Additional docs",    single: false },
};

export function expKey(exp) {
    // Mirror the same key logic used server-side + timeline notes.
    return `${exp?.company || ""}|${exp?.title || ""}|${exp?.start_date || exp?.start || ""}`;
}

export function countDocs(bundle) {
    if (!bundle) return 0;
    let n = 0;
    for (const slot of Object.keys(SLOT_META)) {
        if (SLOT_META[slot].single) { if (bundle[slot]) n++; }
        else                        { n += (bundle[slot]?.length || 0); }
    }
    return n;
}

export function ExperienceDocsChip({ candidateId, experience, bundle, onRefresh, canEdit = true }) {
    const [open, setOpen] = useState(false);
    const count = countDocs(bundle);
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${count > 0 ? "border-ikb text-ikb bg-ikb/5" : "border-dashed border-border text-muted-foreground hover:text-foreground"}`}
                    data-testid={`exp-docs-chip-${expKey(experience).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    title={canEdit ? "Manage employment documents" : "View employment documents"}
                >
                    <Paperclip className="w-3 h-3" />
                    {count > 0 ? `${count} docs` : "add docs"}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-[420px] p-3" align="start">
                <ExperienceDocsCard
                    candidateId={candidateId}
                    experience={experience}
                    bundle={bundle}
                    onRefresh={onRefresh}
                    canEdit={canEdit}
                    compact
                />
            </PopoverContent>
        </Popover>
    );
}

export function ExperienceDocsCard({ candidateId, experience, bundle, onRefresh, canEdit = true, compact = false }) {
    const key = expKey(experience);
    return (
        <div className={`space-y-2 ${compact ? "" : "border border-border p-3 bg-white"}`} data-testid={`exp-docs-card-${key.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            {!compact && (
                <div className="flex items-center gap-2 pb-2 border-b border-border">
                    <FileText className="w-4 h-4" />
                    <div>
                        <div className="text-sm font-semibold">{experience?.title || "Role"}</div>
                        <div className="text-[11px] text-muted-foreground">{experience?.company || "—"}</div>
                    </div>
                </div>
            )}
            {Object.entries(SLOT_META).map(([slot, meta]) => (
                <SlotRow
                    key={slot}
                    slot={slot}
                    label={meta.label}
                    single={meta.single}
                    value={meta.single ? (bundle?.[slot] || null) : (bundle?.[slot] || [])}
                    candidateId={candidateId}
                    expKey={key}
                    onRefresh={onRefresh}
                    canEdit={canEdit}
                />
            ))}
        </div>
    );
}

function SlotRow({ slot, label, single, value, candidateId, expKey, onRefresh, canEdit }) {
    const [busy, setBusy] = useState(false);
    const [linkMode, setLinkMode] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");
    const fileRef = useRef(null);

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/candidates/${candidateId}/experience-documents/${encodeURIComponent(expKey)}/${slot}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`${label} uploaded`);
            onRefresh?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    const addLink = async () => {
        if (!linkUrl.trim()) return;
        setBusy(true);
        try {
            await api.post(`/candidates/${candidateId}/experience-documents/${encodeURIComponent(expKey)}/${slot}/link`, { url: linkUrl.trim() });
            toast.success(`${label} linked`);
            setLinkUrl("");
            // For list slots we keep the link mode open so recruiters can
            // rapidly paste multiple URLs (e.g. 3 recommendation letters)
            // without re-clicking the Link button between adds.
            if (single) setLinkMode(false);
            onRefresh?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const removeItem = async (docId) => {
        setBusy(true);
        try {
            const path = single
                ? `/candidates/${candidateId}/experience-documents/${encodeURIComponent(expKey)}/${slot}`
                : `/candidates/${candidateId}/experience-documents/${encodeURIComponent(expKey)}/${slot}/${docId}`;
            await api.delete(path);
            onRefresh?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const items = single ? (value ? [value] : []) : value;

    return (
        <div className="text-xs" data-testid={`exp-docs-slot-${slot}`}>
            <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-foreground/80">{label}</span>
                {canEdit && (
                    <div className="flex items-center gap-1">
                        <input
                            ref={fileRef}
                            type="file"
                            className="hidden"
                            onChange={(e) => upload(e.target.files?.[0])}
                            data-testid={`exp-docs-file-${slot}`}
                        />
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            disabled={busy}
                            className="h-6 px-1.5 border border-border text-[10px] hover:bg-secondary inline-flex items-center gap-1"
                            title="Upload file"
                            data-testid={`exp-docs-upload-${slot}`}
                        >
                            <Upload className="w-3 h-3" /> Upload
                        </button>
                        <button
                            type="button"
                            onClick={() => setLinkMode((v) => !v)}
                            className="h-6 px-1.5 border border-border text-[10px] hover:bg-secondary inline-flex items-center gap-1"
                            title="Add URL"
                            data-testid={`exp-docs-linkmode-${slot}`}
                        >
                            <Link2 className="w-3 h-3" /> Link
                        </button>
                    </div>
                )}
            </div>
            {linkMode && canEdit && (
                <div className="flex gap-1 mb-1">
                    <Input
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        placeholder="https://…"
                        className="rounded-none h-7 text-xs flex-1"
                        onKeyDown={(e) => { if (e.key === "Enter") addLink(); }}
                        data-testid={`exp-docs-link-url-${slot}`}
                    />
                    <Button onClick={addLink} disabled={busy} className="rounded-none h-7 text-xs px-2" data-testid={`exp-docs-link-save-${slot}`}>Add</Button>
                </div>
            )}
            {items.length === 0 && (
                <div className="text-[11px] text-muted-foreground italic">Not uploaded</div>
            )}
            {items.map((it) => (
                <div key={it.id} className="flex items-center gap-2 border border-border px-2 py-1 mb-1 bg-white group" data-testid={`exp-docs-item-${it.id}`}>
                    <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <a
                        href={it.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 min-w-0 truncate hover:text-ikb text-[11px]"
                        title={it.filename}
                    >
                        {it.filename || it.url}
                    </a>
                    {single && it.red_flag_scan && (
                        <RedFlagChip
                            scan={it.red_flag_scan}
                            candidateId={candidateId}
                            expKey={expKey}
                            slot={slot}
                            onRescan={onRefresh}
                        />
                    )}
                    <span className="text-[9px] text-muted-foreground shrink-0">
                        {it.uploaded_by_role || "?"}{it.uploaded_at ? ` · ${new Date(it.uploaded_at).toLocaleDateString()}` : ""}
                    </span>
                    <a href={it.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                        <ExternalLink className="w-3 h-3" />
                    </a>
                    {canEdit && (
                        <button
                            type="button"
                            onClick={() => removeItem(it.id)}
                            className="text-muted-foreground hover:text-rose-700"
                            title="Remove"
                            data-testid={`exp-docs-remove-${it.id}`}
                        >
                            <X className="w-3 h-3" />
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}
